import { useLocale, type Locale } from "next-intl";
import { defaultLocale } from "@/config";
import { getLocale } from "next-intl/server";


export type Localization
  <TMessage = string, TNonStandardLocale extends string = never> =
{
  [k in Locale | TNonStandardLocale]: TMessage;
};


export type Translator = (
  <TMessage = string, TNonStandardLocale extends string = never>
    (messages: Localization<TMessage, TNonStandardLocale>) =>
      TMessage
);


export function useInlineTranslator
  <TNonStandardLocale extends string = never>
    (override?: { locale: Locale, nonStandardLocale?: { code: string } })
{
  const innerLocale = useLocale();
  const nonStandardLocale = override?.nonStandardLocale?.code as TNonStandardLocale | undefined;
  const locale = nonStandardLocale ?? override?.locale ?? innerLocale;

  return function translate<TMessage = string>(messages: Localization<TMessage, TNonStandardLocale>) {
    return messages[locale] ?? messages[defaultLocale];
  }
}


export async function getInlineTranslator
  <TNonStandardLocale extends string = never>
    (override?: { locale: Locale, nonStandardLocale?: { code: string } })
{
  const nonStandardLocale = override?.nonStandardLocale?.code as TNonStandardLocale | undefined;
  const locale = nonStandardLocale ?? override?.locale ?? await getLocale();

  return function translate<TMessage = string>(messages: Localization<TMessage, TNonStandardLocale>) {
    return messages[locale] ?? messages[defaultLocale];
  }
}
